import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx, appendPercent } from '../WidgetEditComponents/LayoutUtils';
import LayoutEngine from '../../core/LayoutEngine';
import HighlightText from '../WidgetEditComponents/HighlightText';
import AIRefreshIcon from '../../components/AIRefreshIcon';
import { renderRichText, isRichTextEmpty } from '../utils/richTextUtils'; // ✅ Rich Text Utilities

function VideoTeaser({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // ✅ INLINE-EDITING SYSTEM - EXAKT WIE HEROHEADER
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    
    // Image Upload States
    const [isUploadingHeader, setIsUploadingHeader] = useState(false);
    const [isUploadingIcon, setIsUploadingIcon] = useState(false);
    
    // Animation Trigger State (2025 Animation System)
    const [animationKey, setAnimationKey] = useState(0);
    
    // Mobile Layout State - MUSS FRÜH definiert werden
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    
    // Ref für Outside-Click Detection
    const widgetRef = useRef(null);

    // Animation Trigger Effect (2025 Animation System)
    useEffect(() => {
        if (data.triggerAnimation && isEditMode) {
            setAnimationKey(prev => prev + 1);
        }
    }, [data.triggerAnimation, isEditMode]);

    // Delete Handler
    const handleDelete = (event) => {
        event.stopPropagation();
        if (onRemove) {
            onRemove({ stopPropagation: () => {} }, data.id);
        }
        setShowDeleteModal(false);
    };

    // ✅ SIMPLIFIED: Container-basierte Breakpoint-Erkennung (Standard wie alle Widgets)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750); // Standard 750px Breakpoint
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Inline Editing Functions - EXAKT WIE HEROHEADER
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        // Toggle functionality: If already editing this field, cancel editing
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = (fieldName) => {
        if (!onUpdate || !fieldName || fieldName !== isInlineEditing) return;
        
        // Update the widget data
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        
        // Reset inline editing state
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineKeyPress = (e, fieldName) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleInlineSubmit(fieldName);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleInlineCancel();
        }
    };

    // Smart Outside-Click Handler - ORIGINAL BEIBEHALTEN
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            if (isRealOutsideClick) {
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
                    onUpdate(updatedData);
                }
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    const handleButtonClick = () => {
        if (!isEditMode && data.buttonLink) {
            window.open(data.buttonLink, '_blank');
        }
    };

    // ✅ RENDER EDITABLE TEXT FUNCTION - AKTUALISIERT MIT NEUEN STANDARDS
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            return currentValue ? (
                <Element 
                    style={{
                        ...style,
                        whiteSpace: style.whiteSpace || 'pre-wrap'
                    }}
                    onClick={Element === 'button' ? handleButtonClick : undefined}
                >
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            const layoutData = originalLayout;
            const isTextarea = Element !== 'button';
            
            // Action Buttons Container
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: Element === 'button' ? '-11px' : '-11px',
                    right: Element === 'button' ? '-6px' : '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    {/* Grüner Haken - Speichern */}
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    {/* Rotes X - Abbrechen */}
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );
            
            return isTextarea ? (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <textarea
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => {
                            handleInlineKeyPress(e, fieldName);
                            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                setTimeout(() => {
                                    const target = e.target;
                                    target.style.height = 'auto';
                                    target.style.height = target.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        onInput={(e) => {
                            e.target.style.height = 'auto';
                            const originalHeight = parseInt(layoutData.height) || parseInt(style.fontSize) * 1.6;
                            e.target.style.height = Math.max(e.target.scrollHeight, originalHeight) + 'px';
                        }}
                        ref={(textarea) => {
                            if (textarea) {
                                setTimeout(() => {
                                    const originalHeight = parseInt(layoutData.height) || parseInt(style.fontSize) * 1.6;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = Math.max(textarea.scrollHeight, originalHeight) + 'px';
                                    if (textarea.selectionStart === 0 && textarea.selectionEnd === 0) {
                                        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                                    }
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '4px 8px',
                            margin: '0',
                            width: '100%',
                            minHeight: layoutData.height || 'auto',
                            // GRADIENT-FIX: Grauer Text während Bearbeitung
                            color: '#333333',
                            backgroundImage: 'none',
                            backgroundClip: 'unset',
                            webkitBackgroundClip: 'unset',
                            webkitTextFillColor: '#333333',
                            // Original-Text-Eigenschaften
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            lineHeight: style.lineHeight,
                            letterSpacing: style.letterSpacing,
                            textAlign: style.textAlign || 'inherit',
                            whiteSpace: style.whiteSpace || 'pre-wrap'
                        }}
                    />
                    {renderActionButtons()}
                </div>
            ) : (
                // BUTTONS: Input-Felder mit exakter Größen-Erhaltung
                <input
                    type="text"
                    value={inlineValue}
                    onChange={(e) => setInlineValue(e.target.value)}
                    onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                    onBlur={() => handleInlineSubmit(fieldName)}
                    placeholder={placeholder}
                    autoFocus
                    ref={(input) => {
                        if (input) {
                            setTimeout(() => {
                                if (input.selectionStart === 0 && input.selectionEnd === 0) {
                                    input.setSelectionRange(input.value.length, input.value.length);
                                }
                            }, 0);
                        }
                    }}
                    style={{
                        ...style,
                        width: layoutData.width || style.width,
                        height: layoutData.height || style.height,
                        minWidth: layoutData.width || style.minWidth,
                        maxWidth: layoutData.width || 'none',
                        minHeight: layoutData.height || 'auto',
                        maxHeight: layoutData.height || 'none',
                        marginTop: layoutData.marginTop || '0',
                        marginBottom: layoutData.marginBottom || '0',
                        marginLeft: layoutData.marginLeft || '0',
                        marginRight: layoutData.marginRight || '0',
                        backgroundColor: style.backgroundColor,
                        border: style.border || 'none',
                        borderRadius: style.borderRadius,
                        padding: style.padding,
                        display: style.display,
                        alignItems: style.alignItems,
                        justifyContent: style.justifyContent,
                        alignSelf: style.alignSelf,
                        boxShadow: 'none',
                        cursor: 'text',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                />
            );
        }

        // Edit-Modus: Inline-Editing ermöglichen
        const handleElementClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const rect = e.target.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(e.target);
            
            const layoutData = {
                width: rect.width + 'px',
                height: rect.height + 'px',
                marginTop: computedStyle.marginTop,
                marginBottom: computedStyle.marginBottom,
                marginLeft: computedStyle.marginLeft,
                marginRight: computedStyle.marginRight
            };
            
            setOriginalLayout(layoutData);
            handleInlineEdit(fieldName, currentValue);
        };

        return (
            <Element 
                style={{
                    ...style,
                    cursor: Element === 'button' ? 'pointer' : 'text',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    padding: Element === 'button' ? style.padding : '4px 8px',
                    borderRadius: style.borderRadius || (Element === 'button' ? style.borderRadius : '4px'),
                    border: Element === 'button' ? style.border : 'none',
                    boxShadow: Element === 'button' ? style.boxShadow : 'none'
                }}
                onClick={handleElementClick}
                onMouseEnter={(e) => {
                    if (Element !== 'button') {
                        e.target.style.cursor = 'text';
                    } else {
                        e.target.style.cursor = 'pointer';
                    }
                }}
                onMouseLeave={(e) => {
                    e.target.style.cursor = Element === 'button' ? 'pointer' : 'text';
                }}
                title="Click to edit"
            >
                {currentValue || placeholder}
                {/* Blinkender Cursor beim Editieren */}
                {isEditMode && isInlineEditing === fieldName && (
                    <span style={{
                        display: 'inline',
                        animation: 'blink 1s infinite',
                        fontSize: 'inherit',
                        color: 'inherit',
                        marginLeft: '1px'
                    }}>
                        |
                    </span>
                )}
            </Element>
        );
    };

    // Widget Styling
    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                return '2px dashed #000000';
            } else {
                return '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    // ✅ RESPONSIVE WIDGET STYLES mit generateLayoutStyles
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden', // ✅ WICHTIG: Für saubere Header Image Ecken
        width: '100%',
        maxWidth: 'none'
    });

    const widgetStyle = {
        ...layoutStyles,
        // ✅ FALLBACK für alte Height-Property
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(isMobileLayout ? 
                (data.heightMobile || 1600) : // Mobile: Optimierter Default für Video-Widgets
                (data.height || 700)         // Desktop: Template-Default
            )
        })
    };

    // Layout Configuration basierend auf layoutType - RESPONSIVE VERSION
    const getLayoutConfig = () => {
        const layoutType = data.layoutType || 'classic-video-right';
        
        // ✅ KRITISCH: Responsive videoWidth für alle Layouts
        const responsiveVideoWidth = getResponsiveValue(data, 'videoContainerWidth', isMobileLayout, 45);
        
        const layouts = {
            'classic-video-right': {
                contentDirection: 'row',
                videoPosition: 'right',
                textAlign: isMobileLayout ? (data.textAlignmentMobile || data.textAlignment || 'center') : (data.textAlignment || 'left'),
                videoWidth: responsiveVideoWidth,
                textWidth: 100 - responsiveVideoWidth,
                showVideo: true,
                centerContent: false,
                symmetricSpacing: true
            },
            'video-left': {
                contentDirection: 'row-reverse',
                videoPosition: 'left', 
                textAlign: isMobileLayout ? (data.textAlignmentMobile || data.textAlignment || 'center') : (data.textAlignment || 'left'),
                videoWidth: responsiveVideoWidth,
                textWidth: 100 - responsiveVideoWidth,
                showVideo: true,
                centerContent: false,
                symmetricSpacing: true
            },
            'video-dominant': {
                contentDirection: 'row',
                videoPosition: 'right',
                textAlign: isMobileLayout ? (data.textAlignmentMobile || data.textAlignment || 'center') : (data.textAlignment || 'left'),
                videoWidth: responsiveVideoWidth,
                textWidth: 100 - responsiveVideoWidth,
                showVideo: true,
                centerContent: false,
                symmetricSpacing: true
            },
            'video-top-centered': {
                contentDirection: 'column',
                videoPosition: 'top',
                textAlign: isMobileLayout ? (data.textAlignmentMobile || data.textAlignment || 'center') : (data.textAlignment || 'center'),
                videoWidth: responsiveVideoWidth,
                textWidth: 100,
                showVideo: true,
                centerContent: true
            },
            'mixed-centered': {
                contentDirection: 'column',
                videoPosition: 'middle', // Video zwischen Text und Button
                textAlign: isMobileLayout ? (data.textAlignmentMobile || data.textAlignment || 'center') : (data.textAlignment || 'center'),
                videoWidth: responsiveVideoWidth,
                textWidth: 100,
                showVideo: true,
                centerContent: true,
                customOrder: true // ✅ Spezielle Reihenfolge: Text → Video → Button
            }
        };
        
        return layouts[layoutType] || layouts['classic-video-right'];
    };

    // ✅ LAYOUT CONFIG: NACH Funktionsdefinition für responsive Werte
    const layoutConfig = getLayoutConfig();

    // ✅ DEBUG: Console-Log für Video-Debugging (ALLE LAYOUTS) - ERWEITERT
    if (isEditMode) {
        console.log('🎬 VideoTeaser Debug - RESPONSIVE CHECK:', {
            layoutType: data.layoutType,
            showVideo: data.showVideo,
            youtubeUrl: data.youtubeUrl,
            videoPosition: layoutConfig.videoPosition,
            contentDirection: layoutConfig.contentDirection,
            isMobileLayout,
            // ✅ KRITISCHE DEBUG-INFO für Video-Rendering
            videoRenderCondition1: data.showVideo !== false,
            videoRenderCondition2: !!data.youtubeUrl,
            videoWillRender: (data.showVideo !== false && data.youtubeUrl),
            layoutConfigShowVideo: layoutConfig.showVideo,
            // ✅ VIDEO GAP DEBUG
            videoGap: data.videoGap,
            videoGapMobile: data.videoGapMobile,
            contentSpacing: data.contentSpacing,
            contentSpacingMobile: data.contentSpacingMobile,
            usedGap: (layoutConfig.contentDirection === 'column') ? 
                getResponsiveValue(data, 'videoGap', isMobileLayout, 40) : 
                getResponsiveValue(data, 'contentSpacing', isMobileLayout, 20),
            // ✅ VIDEO WIDTH DEBUG - RESPONSIVE
            videoContainerWidth: data.videoContainerWidth,
            videoContainerWidthMobile: data.videoContainerWidthMobile,
            layoutConfigVideoWidth: layoutConfig.videoWidth,
            usedVideoWidth: getResponsiveValue(data, 'videoContainerWidth', isMobileLayout, layoutConfig.videoWidth),
            // ✅ BORDER RADIUS DEBUG - RESPONSIVE
            videoBorderRadius: data.videoBorderRadius,
            videoBorderRadiusMobile: data.videoBorderRadiusMobile,
            usedBorderRadius: getResponsiveValue(data, 'videoBorderRadius', isMobileLayout, 12),
            // ✅ LIVE VALUES: Was wird tatsächlich verwendet?
            actualVideoWidth: layoutConfig.videoWidth,
            actualBorderRadius: getResponsiveValue(data, 'videoBorderRadius', isMobileLayout, 12)
        });
    }

    // YouTube Embed URL Generator
    const getYouTubeEmbedUrl = (url) => {
        if (!url) return '';
        
        const videoIdMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
        if (videoIdMatch) {
            return `https://www.youtube.com/embed/${videoIdMatch[1]}?rel=0&modestbranding=1&showinfo=0`;
        }
        return url;
    };

    return (
        <>
            {/* CSS Animations */}
            <style>
                {`
                    @keyframes blink {
                        0%, 50% { opacity: 1; }
                        51%, 100% { opacity: 0; }
                    }
                    
                    /* 🎬 VIDEO TEASER ANIMATIONS */
                    @keyframes videoTeaserFadeIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    
                    @keyframes videoTeaserSlideIn {
                        from { transform: translateX(-20px); opacity: 0; }
                        to { transform: translateX(0); opacity: 1; }
                    }
                `}
            </style>
            
            <div 
                className={`widget-container video-teaser-widget layout-${data.layoutType || 'classic-video-right'}${isMobileLayout ? ' mobile-layout' : ''}`}
                style={widgetStyle} 
                key={animationKey} // ✅ Animation System Integration
                ref={(el) => {
                    containerRef.current = el;
                    widgetRef.current = el;
                }}
            >
                {/* Delete Button */}
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                
                {/* 🤖 AI REFRESH ICON */}
                {isEditMode && (
                    <AIRefreshIcon 
                        widget={{
                            ...data,
                            type: 'VideoTeaser'
                        }}
                        spaceId={null}
                        onContentRefreshed={(refreshedWidget) => {
                            console.log('🤖 [VIDEOTEASER] AI-refreshed content received:', refreshedWidget);
                            onUpdate(refreshedWidget);
                        }}
                        position="top-left"
                    />
                )}
                {isEditMode && showDeleteModal && (
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={(event) => handleDelete(event, data.id)}
                    />
                )}
                
                {/* Header Image - BANNER STYLE: Klebt oben am Container */}
                {data.showHeaderImage && data.headerImage && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        width: '100%',
                        height: appendPx(getResponsiveValue(data, 'headerImageHeight', isMobileLayout, 120)),
                        backgroundImage: `url(${data.headerImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: data.borderRadius ? `${appendPx(data.borderRadius)} ${appendPx(data.borderRadius)} 0 0` : '0',
                        zIndex: 1
                    }} />
                )}
                
                {/* Main Content Container */}
                <div style={{
                    display: 'flex',
                    flexDirection: isMobileLayout ? 'column' : layoutConfig.contentDirection,
                    alignItems: isMobileLayout || layoutConfig.centerContent || layoutConfig.symmetricSpacing ? 'center' : 'flex-start',
                    justifyContent: layoutConfig.centerContent ? 'center' : 'flex-start', // ✅ CENTERED LAYOUTS: Center content vertically
                    width: '100%',
                    // ✅ SYMMETRIC CONTAINER PADDING: Gleiche Abstände zu allen Rändern
                    padding: data.showHeaderImage && data.headerImage ? 
                        // Mit Header Image: Top-Padding = Header Height + Spacing + Standard Padding
                        `${getResponsiveValue(data, 'headerImageHeight', isMobileLayout, 120) + getResponsiveValue(data, 'headerSpacing', isMobileLayout, 40) + (isMobileLayout ? 20 : 40)}px ${isMobileLayout ? '20px' : '60px'} ${isMobileLayout ? '20px' : '40px'}` :
                        // Ohne Header Image: Standard Padding
                        (isMobileLayout ? '20px' : '40px 60px'),
                    position: 'relative',
                    zIndex: 2,
                    // ✅ UNIVERSAL GAP: Funktioniert für alle Layout-Richtungen
                    gap: isMobileLayout ? '40px' : 
                         (layoutConfig.contentDirection === 'column') ? 
                             appendPx(getResponsiveValue(data, 'videoGap', isMobileLayout, 40)) : // ✅ Centered layouts: videoGap
                             appendPx(getResponsiveValue(data, 'contentSpacing', isMobileLayout, 20)), // Side-by-side layouts: contentSpacing
                    // ✅ AUTO HEIGHT: Keine feste minHeight mehr - passt sich an Inhalt an
                    minHeight: 'auto'
                }}>
                    {/* ✅ MIXED CENTERED LAYOUT: Spezielle Reihenfolge Text → Video → Button */}
                    {layoutConfig.customOrder ? (
                        <>
                            {/* Text Section (ohne Button) */}
                            <div style={{
                                width: '100%',
                                maxWidth: '800px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                gap: appendPx(getResponsiveValue(data, 'contentSpacing', isMobileLayout, 20)),
                                padding: '0'
                            }}>
                                {/* Icon */}
                                {data.showIcon && data.iconImage && (
                                    <img 
                                        src={data.iconImage} 
                                        alt="Icon"
                                        style={{
                                            height: appendPx(getResponsiveValue(data, 'iconHeight', isMobileLayout, 60)),
                                            width: 'auto',
                                            marginBottom: '10px'
                                        }}
                                    />
                                )}
                                
                                {/* Rich Text - Inline Editable mit HighlightText Support */}
                                {data.showRichText && (
                                    <div style={{
                                        width: appendPx(data.richTextMaxWidth || 600) // ✅ Width Container (funktioniert!)
                                    }}>
                                        {/* ✅ CONDITIONAL: Rich Text Editor ODER Highlights */}
                                        {(!data.richTextHighlightWords || data.richTextHighlightWords.trim() === '') ? (
                                            // 🔧 RICH TEXT EDITOR RENDERING (ohne Highlights) - NEUE UTILS
                                            <div 
                                                {...renderRichText(
                                                    data.richText || 'Enter rich text with <strong>bold</strong> formatting...',
                                                    {
                                                        ...((!data.richTextEffect || data.richTextEffect === 'none') && {
                                                            color: data.richTextColor || '#2c3e50'
                                                        }),
                                                        // fontSize und fontWeight werden über CSS Custom Properties + responsive CSS-Klassen gesteuert
                                                        // fontSize: appendPx(getResponsiveValue(data, 'richTextSize', isMobileLayout, 28)),
                                                        // fontWeight: getResponsiveValue(data, 'richTextWeight', isMobileLayout, '600'),
                                                        fontFamily: data.richTextFont || fonts.globalBigFont,
                                                        marginBottom: '0',
                                                        lineHeight: '1.3',
                                                        textAlign: isMobileLayout ? 
                                                           (data.textAlignmentMobile || data.textAlignment || 'center') : 
                                                           (data.textAlignment || layoutConfig.textAlign),
                                                        ...getTextEffectStyles(data.richTextEffect),
                                                        // ✅ TEXT-REFACTORING 2025: Responsive Font-Sizes + Weights
                                                        '--mobile-size': appendPx(data.richTextSizeMobile || data.richTextSize || 28),
                                                        '--normal-size': appendPx(data.richTextSize || 28),
                                                        '--big-size': appendPx(data.richTextSizeBig || data.richTextSize || 28),
                                                        '--mobile-weight': data.richTextWeightMobile || data.richTextWeight || '600',
                                                        '--normal-weight': data.richTextWeight || '600',
                                                        '--big-weight': data.richTextWeightBig || data.richTextWeight || '600'
                                                    },
                                                    'rich-text-output responsive-rich-text' // ✅ CSS-Klassen für Font-Inheritance + Responsive
                                                )}
                                            />
                                        ) : (
                                            // 🎨 HIGHLIGHT-MODUS mit HTML-Content Support
                                            <div style={{
                                                ...((!data.richTextEffect || data.richTextEffect === 'none') && {
                                                    color: data.richTextColor || '#2c3e50'
                                                }),
                                                // fontSize und fontWeight werden über CSS Custom Properties + responsive CSS-Klassen gesteuert
                                                // fontSize: appendPx(getResponsiveValue(data, 'richTextSize', isMobileLayout, 28)),
                                                // fontWeight: getResponsiveValue(data, 'richTextWeight', isMobileLayout, '600'),
                                                fontFamily: data.richTextFont || fonts.globalBigFont,
                                                marginBottom: '0',
                                                lineHeight: '1.3',
                                                whiteSpace: 'pre-wrap',
                                                ...getTextEffectStyles(data.richTextEffect),
                                                // Responsive Font-Sizes
                                                '--mobile-size': appendPx(data.richTextSizeMobile || data.richTextSize || 28),
                                                '--normal-size': appendPx(data.richTextSize || 28),
                                                '--big-size': appendPx(data.richTextSizeBig || data.richTextSize || 28)
                                            }}>
                                                <HighlightText
                                                    text={data.richText}
                                                    highlightWords={data.richTextHighlightWords}
                                                    baseStyle={{}}
                                                    highlightFont={data.richTextHighlightFont || fonts.globalSmallFont}
                                                    highlightColor={data.richTextHighlightColor}
                                                    highlightWeight={data.richTextHighlightWeight}
                                                    highlightSize={data.richTextHighlightSize || data.richTextSize}
                                                    highlightSizeMobile={data.richTextHighlightSizeMobile || data.richTextSizeMobile}
                                                    highlightSizeBig={data.richTextHighlightSizeBig || data.richTextSizeBig}
                                                    highlightEffect={data.richTextHighlightEffect}
                                                    highlightMarginLeft={data.richTextHighlightMarginLeft}
                                                    highlightMarginRight={data.richTextHighlightMarginRight}
                                                    getTextEffectStyles={getTextEffectStyles}
                                                    isHtmlContent={true} // ✅ HTML-Content Support aktivieren
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Description Text entfernt - wird nach Video gerendert */}
                            </div>

                            {/* Video Section */}
                            {data.showVideo !== false && data.youtubeUrl && (
                                <div style={{
                                    width: `${getResponsiveValue(data, 'videoContainerWidth', isMobileLayout, 70)}%`,
                                    maxWidth: '800px',
                                    padding: '0',
                                    position: 'relative',
                                    margin: '0 auto'
                                }}>
                                    <div style={{
                                        position: 'relative',
                                        width: '100%',
                                        paddingBottom: '56.25%', // 16:9 Aspect Ratio
                                        height: 0,
                                        borderRadius: appendPx(data.videoBorderRadius !== undefined ? 
                                            getResponsiveValue(data, 'videoBorderRadius', isMobileLayout, 0) : 12),
                                        overflow: 'visible',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        {/* ✅ DECORATION IMAGE - Absolute zum Video Container */}
                                        {data.showDecorationImage && data.decorationImage && (() => {
                                            const posX = data.decorationImagePositionX !== undefined ? data.decorationImagePositionX : 80;
                                            const posY = data.decorationImagePositionY !== undefined ? data.decorationImagePositionY : 20;
                                            const adjustedPosY = posY - 50;
                                            
                                            return (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: `${adjustedPosY}%`,
                                                    left: `${posX}%`,
                                                    width: appendPx(getResponsiveValue(data, 'decorationImageSize', isMobileLayout, 120)),
                                                    height: appendPx(getResponsiveValue(data, 'decorationImageSize', isMobileLayout, 120)),
                                                    backgroundImage: `url(${data.decorationImage})`,
                                                    backgroundSize: 'contain',
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'center',
                                                    opacity: (data.decorationImageOpacity || 30) / 100,
                                                    zIndex: -1,
                                                    pointerEvents: 'none',
                                                    transform: 'none'
                                                }} />
                                            );
                                        })()}
                                        <iframe
                                            src={getYouTubeEmbedUrl(data.youtubeUrl)}
                                            title="Video"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                zIndex: 1
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ✅ DESCRIPTION TEXT SECTION - Zwischen Video und Button */}
                            {data.showDescriptionText && (
                                <div style={{
                                    width: appendPx(data.descriptionMaxWidth || 500), // ✅ Width Container (wie Rich Text)
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    padding: '0',
                                    margin: '0 auto'
                                }}>
                                    <div 
                                        {...renderRichText(
                                            data.descriptionText || 'Enter description with <strong>bold</strong> formatting...',
                                            {
                                                ...((!data.descriptionEffect || data.descriptionEffect === 'none') && {
                                                    color: data.descriptionColor || '#666666'
                                                }),
                                                // fontSize und fontWeight werden über CSS Custom Properties + responsive CSS-Klassen gesteuert
                                                // fontSize: appendPx(getResponsiveValue(data, 'descriptionSize', isMobileLayout, 16)),
                                                // fontWeight: getResponsiveValue(data, 'descriptionWeight', isMobileLayout, '400'),
                                                fontFamily: data.descriptionFont || fonts.globalSmallFont,
                                                marginBottom: '0',
                                                lineHeight: '1.6',
                                                textAlign: isMobileLayout ? 
                                                   (data.textAlignmentMobile || data.textAlignment || 'center') : 
                                                   (data.textAlignment || layoutConfig.textAlign),
                                                ...getTextEffectStyles(data.descriptionEffect),
                                                // ✅ TEXT-REFACTORING 2025: Responsive Font-Sizes + Weights
                                                '--mobile-size': appendPx(data.descriptionSizeMobile || data.descriptionSize || 16),
                                                '--normal-size': appendPx(data.descriptionSize || 16),
                                                '--big-size': appendPx(data.descriptionSizeBig || data.descriptionSize || 16),
                                                '--mobile-weight': data.descriptionWeightMobile || data.descriptionWeight || '400',
                                                '--normal-weight': data.descriptionWeight || '400',
                                                '--big-weight': data.descriptionWeightBig || data.descriptionWeight || '400'
                                            },
                                            'rich-text-output responsive-description-text' // ✅ CSS-Klassen für Font-Inheritance + Responsive
                                        )}
                                    />
                                </div>
                            )}

                            {/* Button Section - Separat nach dem Video */}
                            {data.showButtonText && (
                                <div style={{
                                    width: '100%',
                                    maxWidth: '800px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    margin: '0 auto'
                                }}>
                                    {renderEditableText(
                                        'buttonText',
                                        data.buttonText,
                                        'button',
                                        {
                                            ...((!data.buttonEffect || data.buttonEffect === 'none') && {
                                                backgroundColor: data.buttonColor || '#5C80BC',
                                                color: data.buttonTextColor || '#FFFFFF'
                                            }),
                                            ...getButtonEffectStyles(data.buttonEffect),
                                            fontSize: isMobileLayout ? appendPx(data.buttonTextSizeMobile || data.buttonTextSize || 18) : appendPx(data.buttonTextSize || 18),
                                            fontWeight: isMobileLayout ? (data.buttonTextWeightMobile || data.buttonTextWeight || '600') : (data.buttonTextWeight || '600'),
                                            fontFamily: data.buttonFont || fonts.globalSmallFont,
                                            
                                            // ✅ BUTTON WIDTH SYSTEM - Exakt wie HeroHeader
                                            width: isMobileLayout ? 
                                                appendPercent(data.buttonWidthMobile || data.buttonWidth || 100) : // Mobile: % + maxWidth
                                                (data.buttonWidthDesktop ? appendPx(data.buttonWidthDesktop) : appendPercent(data.buttonWidth || 80)), // Desktop: Pixel oder Fallback %
                                            maxWidth: isMobileLayout ? 
                                                appendPx(data.buttonMaxWidthMobile || data.buttonMaxWidth || 300) : // Mobile: maxWidth
                                                (data.buttonMaxWidth ? appendPx(data.buttonMaxWidth) : 'none'), // Desktop: Optional maxWidth
                                            height: isMobileLayout ? appendPx(data.buttonHeightMobile || data.buttonHeight || 50) : appendPx(data.buttonHeight || 50),
                                            
                                            padding: `${data.buttonPaddingY || 14}px ${data.buttonPaddingX || 28}px`,
                                            borderRadius: isMobileLayout ? appendPx(data.buttonBorderRadiusMobile || data.buttonBorderRadius || 8) : appendPx(data.buttonBorderRadius || 8),
                                            cursor: isEditMode ? 'pointer' : 'pointer',
                                            transition: 'all 0.3s ease',
                                            border: 'none',
                                            marginTop: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        },
                                        'Enter button text...'
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        // ✅ NORMALE LAYOUTS: Überarbeitete Container-Struktur
                        <>
                            {/* Text Container */}
                            <div className="video-teaser-text-container" style={{
                                width: isMobileLayout ? '100%' : 
                                       (layoutConfig.contentDirection === 'column') ? '100%' : 
                                       `${100 - getResponsiveValue(data, 'videoContainerWidth', isMobileLayout, layoutConfig.videoWidth)}%`,
                                maxWidth: layoutConfig.contentDirection === 'column' ? '800px' : 'none',
                                margin: layoutConfig.contentDirection === 'column' ? '0 auto' : '0',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: (() => {
                                    const effectiveAlignment = isMobileLayout ? 
                                        (data.textAlignmentMobile || data.textAlignment || 'center') : 
                                        (data.textAlignment || layoutConfig.textAlign || 'left');
                                    
                                    return effectiveAlignment === 'center' ? 'center' : 
                                           effectiveAlignment === 'right' ? 'flex-end' : 'flex-start';
                                })(),
                                textAlign: isMobileLayout ? 
                                   (data.textAlignmentMobile || data.textAlignment || 'center') : 
                                   (data.textAlignment || layoutConfig.textAlign),
                                gap: appendPx(getResponsiveValue(data, 'contentSpacing', isMobileLayout, 20))
                            }}>
                                {/* Icon */}
                                {data.showIcon && data.iconImage && (
                                    <img 
                                        src={data.iconImage} 
                                        alt="Icon"
                                        style={{
                                            height: appendPx(getResponsiveValue(data, 'iconHeight', isMobileLayout, 60)),
                                            width: 'auto',
                                        }}
                                    />
                                )}
                                
                                {/* Rich Text - Inline Editable mit HighlightText Support */}
                                {data.showRichText && (
                                    <div style={{
                                        width: appendPx(data.richTextMaxWidth || 600) // ✅ Width Container (funktioniert!)
                                    }}>
                                        {/* ✅ CONDITIONAL: Rich Text Editor ODER Highlights */}
                                        {(!data.richTextHighlightWords || data.richTextHighlightWords.trim() === '') ? (
                                            // 🔧 RICH TEXT EDITOR RENDERING (ohne Highlights) - NEUE UTILS
                                            <div 
                                                {...renderRichText(
                                                    data.richText || 'Enter rich text with <strong>bold</strong> formatting...',
                                                    {
                                                        ...((!data.richTextEffect || data.richTextEffect === 'none') && {
                                                            color: data.richTextColor || '#2c3e50'
                                                        }),
                                                        // fontSize und fontWeight werden über CSS Custom Properties + responsive CSS-Klassen gesteuert
                                                        // fontSize: appendPx(getResponsiveValue(data, 'richTextSize', isMobileLayout, 28)),
                                                        // fontWeight: getResponsiveValue(data, 'richTextWeight', isMobileLayout, '600'),
                                                        fontFamily: data.richTextFont || fonts.globalBigFont,
                                                        marginBottom: '0',
                                                        lineHeight: '1.3',
                                                        textAlign: isMobileLayout ? 
                                                           (data.textAlignmentMobile || data.textAlignment || 'center') : 
                                                           (data.textAlignment || layoutConfig.textAlign),
                                                        ...getTextEffectStyles(data.richTextEffect),
                                                        // ✅ TEXT-REFACTORING 2025: Responsive Font-Sizes + Weights
                                                        '--mobile-size': appendPx(data.richTextSizeMobile || data.richTextSize || 28),
                                                        '--normal-size': appendPx(data.richTextSize || 28),
                                                        '--big-size': appendPx(data.richTextSizeBig || data.richTextSize || 28),
                                                        '--mobile-weight': data.richTextWeightMobile || data.richTextWeight || '600',
                                                        '--normal-weight': data.richTextWeight || '600',
                                                        '--big-weight': data.richTextWeightBig || data.richTextWeight || '600'
                                                    },
                                                    'rich-text-output responsive-rich-text' // ✅ CSS-Klassen für Font-Inheritance + Responsive
                                                )}
                                            />
                                        ) : (
                                            // 🎨 HIGHLIGHT-MODUS mit HTML-Content Support
                                            <div style={{
                                                ...((!data.richTextEffect || data.richTextEffect === 'none') && {
                                                    color: data.richTextColor || '#2c3e50'
                                                }),
                                                // fontSize und fontWeight werden über CSS Custom Properties + responsive CSS-Klassen gesteuert
                                                // fontSize: appendPx(getResponsiveValue(data, 'richTextSize', isMobileLayout, 28)),
                                                // fontWeight: getResponsiveValue(data, 'richTextWeight', isMobileLayout, '600'),
                                                fontFamily: data.richTextFont || fonts.globalBigFont,
                                                marginBottom: '0',
                                                lineHeight: '1.3',
                                                whiteSpace: 'pre-wrap',
                                                ...getTextEffectStyles(data.richTextEffect),
                                                // Responsive Font-Sizes
                                                '--mobile-size': appendPx(data.richTextSizeMobile || data.richTextSize || 28),
                                                '--normal-size': appendPx(data.richTextSize || 28),
                                                '--big-size': appendPx(data.richTextSizeBig || data.richTextSize || 28)
                                            }}>
                                                <HighlightText
                                                    text={data.richText}
                                                    highlightWords={data.richTextHighlightWords}
                                                    baseStyle={{}}
                                                    highlightFont={data.richTextHighlightFont || fonts.globalSmallFont}
                                                    highlightColor={data.richTextHighlightColor}
                                                    highlightWeight={data.richTextHighlightWeight}
                                                    highlightSize={data.richTextHighlightSize || data.richTextSize}
                                                    highlightSizeMobile={data.richTextHighlightSizeMobile || data.richTextSizeMobile}
                                                    highlightSizeBig={data.richTextHighlightSizeBig || data.richTextSizeBig}
                                                    highlightEffect={data.richTextHighlightEffect}
                                                    highlightMarginLeft={data.richTextHighlightMarginLeft}
                                                    highlightMarginRight={data.richTextHighlightMarginRight}
                                                    getTextEffectStyles={getTextEffectStyles}
                                                    isHtmlContent={true} // ✅ HTML-Content Support aktivieren
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Second Video - Between Title and Description */}
                                {data.showSecondVideo && data.secondVideoUrl && (
                                    <div style={{
                                        width: `${getResponsiveValue(data, 'secondVideoWidth', isMobileLayout, 60)}%`,
                                        maxWidth: '600px',
                                        margin: '0 auto'
                                    }}>
                                        <div style={{
                                            position: 'relative',
                                            width: '100%',
                                            paddingBottom: '56.25%',
                                            height: 0,
                                            borderRadius: appendPx(getResponsiveValue(data, 'secondVideoBorderRadius', isMobileLayout, 8)),
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                        }}>
                                            <iframe
                                                src={getYouTubeEmbedUrl(data.secondVideoUrl)}
                                                title="Second Video"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Description Text - Inline Editable */}
                                {data.showDescriptionText && renderEditableText(
                                    'descriptionText',
                                    data.descriptionText,
                                    'p',
                                    {
                                        ...((!data.descriptionEffect || data.descriptionEffect === 'none') && {
                                            color: data.descriptionColor || '#666666'
                                        }),
                                        fontSize: appendPx(getResponsiveValue(data, 'descriptionSize', isMobileLayout, 16)),
                                        fontWeight: getResponsiveValue(data, 'descriptionWeight', isMobileLayout, '400'),
                                        fontFamily: data.descriptionFont || fonts.globalSmallFont,
                                        marginBottom: '0',
                                        lineHeight: '1.6',
                                        ...getTextEffectStyles(data.descriptionEffect)
                                    },
                                    'Enter description...'
                                )}

                                {/* Button - Inline Editable */}
                                {data.showButtonText && renderEditableText(
                                    'buttonText',
                                    data.buttonText,
                                    'button',
                                    {
                                        ...((!data.buttonEffect || data.buttonEffect === 'none') && {
                                            backgroundColor: data.buttonColor || '#5C80BC',
                                            color: data.buttonTextColor || '#FFFFFF'
                                        }),
                                        ...getButtonEffectStyles(data.buttonEffect),
                                        fontSize: isMobileLayout ? appendPx(data.buttonTextSizeMobile || data.buttonTextSize || 18) : appendPx(data.buttonTextSize || 18),
                                        fontWeight: isMobileLayout ? (data.buttonTextWeightMobile || data.buttonTextWeight || '600') : (data.buttonTextWeight || '600'),
                                        fontFamily: data.buttonFont || fonts.globalSmallFont,
                                        
                                        // ✅ BUTTON WIDTH SYSTEM - Exakt wie HeroHeader
                                        width: isMobileLayout ? 
                                            appendPercent(data.buttonWidthMobile || data.buttonWidth || 100) : // Mobile: % + maxWidth
                                            (data.buttonWidthDesktop ? appendPx(data.buttonWidthDesktop) : appendPercent(data.buttonWidth || 80)), // Desktop: Pixel oder Fallback %
                                        maxWidth: isMobileLayout ? 
                                            appendPx(data.buttonMaxWidthMobile || data.buttonMaxWidth || 300) : // Mobile: maxWidth
                                            (data.buttonMaxWidth ? appendPx(data.buttonMaxWidth) : 'none'), // Desktop: Optional maxWidth
                                        height: isMobileLayout ? appendPx(data.buttonHeightMobile || data.buttonHeight || 50) : appendPx(data.buttonHeight || 50),
                                        
                                        padding: `${data.buttonPaddingY || 14}px ${data.buttonPaddingX || 28}px`,
                                        borderRadius: isMobileLayout ? appendPx(data.buttonBorderRadiusMobile || data.buttonBorderRadius || 8) : appendPx(data.buttonBorderRadius || 8),
                                        cursor: isEditMode ? 'pointer' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        alignSelf: (() => {
                                            // ✅ RESPONSIVE BUTTON ALIGNMENT wie HeroHeader
                                            const effectiveAlignment = isMobileLayout ? 
                                                (data.textAlignmentMobile || data.textAlignment || 'center') : // Mobile
                                                (data.textAlignment || layoutConfig.textAlign || 'left'); // Desktop
                                            
                                            return effectiveAlignment === 'center' ? 'center' : 
                                                   effectiveAlignment === 'right' ? 'flex-end' : 'flex-start';
                                        })(),
                                        border: 'none',
                                        marginTop: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    },
                                    'Enter button text...'
                                )}
                            </div>

                            {/* Video Container */}
                            {data.showVideo !== false && data.youtubeUrl && (
                                <div className="video-teaser-video-container" style={{
                                    width: (layoutConfig.contentDirection === 'column') ? 
                                           `${getResponsiveValue(data, 'videoContainerWidth', isMobileLayout, layoutConfig.videoWidth)}%` : 
                                           (isMobileLayout ? '100%' : `${getResponsiveValue(data, 'videoContainerWidth', isMobileLayout, layoutConfig.videoWidth)}%`),
                                    maxWidth: layoutConfig.contentDirection === 'column' ? '800px' : 'none',
                                    margin: layoutConfig.contentDirection === 'column' ? '0 auto' : '0',
                                    order: (layoutConfig.videoPosition === 'top') ? -1 : 
                                           (layoutConfig.videoPosition === 'bottom') ? 1 : 
                                           (isMobileLayout && (layoutConfig.videoPosition === 'right' || layoutConfig.videoPosition === 'left')) ? -1 : 0,
                                    position: 'relative'
                                }}>
                                    <div style={{
                                        position: 'relative', // ✅ RELATIVE: Für absolute Positionierung des Decoration Image
                                        width: '100%',
                                        paddingBottom: '56.25%', // 16:9 Aspect Ratio
                                        height: 0,
                                        borderRadius: appendPx(data.videoBorderRadius !== undefined ? 
                                            getResponsiveValue(data, 'videoBorderRadius', isMobileLayout, 0) : 12),
                                        overflow: 'visible', // ✅ VISIBLE: Decoration Image kann über Video Container hinausragen
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        {/* ✅ DECORATION IMAGE - Absolute zum Video Container */}
                                        {data.showDecorationImage && data.decorationImage && (() => {
                                            const posX = data.decorationImagePositionX !== undefined ? data.decorationImagePositionX : 80;
                                            const posY = data.decorationImagePositionY !== undefined ? data.decorationImagePositionY : 20;
                                            
                                            // ✅ Y-POSITION SHIFT: Verschiebe das gesamte Element nach oben
                                            const adjustedPosY = posY - 50; // Verschiebe um 50% nach oben
                                            
                                            // Debug-Logs entfernt um Loop zu vermeiden
                                            
                                            return (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: `${adjustedPosY}%`,
                                                    left: `${posX}%`,
                                                    width: appendPx(getResponsiveValue(data, 'decorationImageSize', isMobileLayout, 120)),
                                                    height: appendPx(getResponsiveValue(data, 'decorationImageSize', isMobileLayout, 120)),
                                                    backgroundImage: `url(${data.decorationImage})`,
                                                    backgroundSize: 'contain',
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'center',
                                                    opacity: (data.decorationImageOpacity || 30) / 100,
                                                    zIndex: -1, // ✅ Hinter dem Video
                                                    pointerEvents: 'none', // ✅ Nicht klickbar
                                                    transform: 'none' // ✅ KEIN TRANSFORM - direkte Positionierung
                                                }} />
                                            );
                                        })()}
                                        <iframe
                                            src={getYouTubeEmbedUrl(data.youtubeUrl)}
                                            title="Video"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                zIndex: 1 // ✅ Video über Decoration Image
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default VideoTeaser;